import React, { useState } from 'react';
import { SlidersHorizontal, X, Check, ArrowDownUp } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export type SortOption = 'featured' | 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc';

interface FilterSortBarProps {
  totalCount: number;
  categories: string[];
  selectedCategory: string;
  onSelectCategory: (category: string) => void;
  selectedSort: SortOption;
  onSelectSort: (sort: SortOption) => void;
  inStockOnly: boolean;
  onToggleInStockOnly: (val: boolean) => void;
}

export default function FilterSortBar({
  totalCount,
  categories,
  selectedCategory,
  onSelectCategory,
  selectedSort,
  onSelectSort,
  inStockOnly,
  onToggleInStockOnly,
}: FilterSortBarProps) {
  const [isOpen, setIsOpen] = useState(false);

  const sortLabels: Record<SortOption, string> = {
    'featured': 'Featured',
    'price-asc': 'Price: Low to High',
    'price-desc': 'Price: High to Low',
    'name-asc': 'Alphabetically: A-Z',
    'name-desc': 'Alphabetically: Z-A',
  };

  const activeFilterCount = (selectedCategory !== 'all' ? 1 : 0) + (inStockOnly ? 1 : 0) + (selectedSort !== 'featured' ? 1 : 0);

  return (
    <>
      {/* Secondary Bar matching Screenshot 1 */}
      <div className="w-full bg-[#FAF7F2] border-b border-[#E5E0D8] px-4 py-2.5 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 text-xs font-bold text-[#1F1F1F] hover:text-[#D97736] transition-colors py-1 cursor-pointer"
        >
          <SlidersHorizontal className="w-3.5 h-3.5 stroke-[2.2]" />
          <span>Filter and sort</span>
          {activeFilterCount > 0 && (
            <span className="w-4 h-4 bg-[#D97736] text-white text-[9px] font-bold rounded-full flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>

        <span className="text-xs font-medium text-[#666666]">
          {totalCount} product{totalCount === 1 ? '' : 's'}
        </span>
      </div>

      {/* Filter & Sort Drawer Modal */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-[2px] cursor-pointer"
            />

            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="relative w-full max-w-xs sm:max-w-sm bg-white h-full z-50 flex flex-col shadow-2xl border-l border-[#E5E0D8]"
            >
              {/* Drawer Header */}
              <div className="p-4 border-b border-[#EBEBEB] flex items-center justify-between bg-[#FAF7F2]">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-[#D97736]" />
                  <h3 className="font-bold text-sm text-[#1F1F1F] uppercase tracking-wider">Filter and Sort</h3>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:text-black transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Drawer Content */}
              <div className="flex-1 overflow-y-auto p-5 space-y-6">
                {/* Sort By Section */}
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-gray-500 mb-3">Sort by</h4>
                  <div className="space-y-1.5">
                    {(Object.keys(sortLabels) as SortOption[]).map((sortKey) => {
                      const isSelected = selectedSort === sortKey;
                      return (
                        <button
                          key={sortKey}
                          onClick={() => onSelectSort(sortKey)}
                          className={`w-full text-left px-3 py-2 rounded text-xs font-semibold flex items-center justify-between transition-colors ${
                            isSelected
                              ? 'bg-[#2B2B2B] text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span>{sortLabels[sortKey]}</span>
                          {isSelected && <Check className="w-3.5 h-3.5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="h-[1px] bg-gray-200" />

                {/* Categories */}
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-gray-500 mb-3">Category</h4>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => onSelectCategory('all')}
                      className={`px-3 py-1.5 text-xs font-bold rounded-sm transition-all ${
                        selectedCategory === 'all'
                          ? 'variant-box-active'
                          : 'variant-box-inactive hover:border-black'
                      }`}
                    >
                      All
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => onSelectCategory(cat)}
                        className={`px-3 py-1.5 text-xs font-bold rounded-sm transition-all ${
                          selectedCategory === cat
                            ? 'variant-box-active'
                            : 'variant-box-inactive hover:border-black'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="h-[1px] bg-gray-200" />

                {/* In Stock Toggle */}
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-gray-500 mb-3">Availability</h4>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inStockOnly}
                      onChange={(e) => onToggleInStockOnly(e.target.checked)}
                      className="w-4 h-4 text-[#D97736] rounded border-gray-300 focus:ring-[#D97736]"
                    />
                    <span className="text-xs font-medium text-gray-800">In stock only</span>
                  </label>
                </div>
              </div>

              {/* Drawer Footer */}
              <div className="p-4 border-t border-[#EBEBEB] bg-[#FAF7F2] flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    onSelectCategory('all');
                    onSelectSort('featured');
                    onToggleInStockOnly(false);
                  }}
                  className="flex-1 py-2.5 px-3 border border-gray-300 rounded text-xs font-bold text-gray-700 hover:border-black transition-colors"
                >
                  Reset all
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="flex-1 py-2.5 px-3 btn-terracotta bg-[#D97736] text-white rounded text-xs font-bold shadow-sm"
                >
                  Apply
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
